// Open shifts — published, unassigned shifts across the department. Default
// filter is "my positions". Each card computes the would-be weekly hours so
// people see the overtime consequence before they raise their hand.
import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../auth/AuthProvider'
import { fetchMyShifts, fetchOpenShifts, fetchPositions, fetchTeams } from '../../api/reads'
import { api } from '../../api/mutations'
import { CallableError } from '../../api/callable'
import { useToast } from '../../components/Toast'
import { EmptyState, ErrorBanner, Spinner } from '../../components/ui'
import { todayLocal } from '../../lib/time'
import { formatDateLong, formatHours, formatTimeRange } from '../../lib/format'
import { crossesThreshold, round2 } from '../../lib/hours'
import { positionColor } from '../../lib/positionColors'
import type { Employee, Shift } from '../../lib/types'

export default function OpenShiftsPage() {
  const { employee, email } = useAuth()
  if (!employee || !email) return null
  if (!employee.departmentId) {
    return <EmptyState title="No department yet" hint="Ask your director to finish your setup." />
  }
  return <OpenShifts employee={employee} email={email} departmentId={employee.departmentId} />
}

/** Normalized claim-error copy branched on CallableError.serverCode. */
function claimErrorMessage(e: unknown): string {
  if (e instanceof CallableError) {
    if (e.serverCode === 'DOUBLE_BOOKED') return 'That overlaps a shift you already work.'
    if (e.serverCode === 'CREDENTIAL_GAP') {
      const raw = e.details.missing
      const missing = Array.isArray(raw) ? raw.filter((m): m is string => typeof m === 'string') : []
      return missing.length > 0
        ? `You're missing a required credential: ${missing.join(', ')}.`
        : "You're missing a required credential for this position."
    }
    if (e.serverCode === 'PENDING_REQUEST') return 'You already have a pending request on this shift.'
    return e.message
  }
  return e instanceof Error ? e.message : 'Something went wrong.'
}

function OpenShifts({
  employee,
  email,
  departmentId,
}: {
  employee: Employee
  email: string
  departmentId: string
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [myPositionsOnly, setMyPositionsOnly] = useState(true)
  const [requestedIds, setRequestedIds] = useState<ReadonlySet<string>>(new Set())
  const [busyId, setBusyId] = useState<string | null>(null)

  const openQuery = useQuery({
    queryKey: ['openShifts', departmentId],
    queryFn: () => fetchOpenShifts(departmentId, todayLocal()),
  })
  const myShiftsQuery = useQuery({
    queryKey: ['myShifts', email],
    queryFn: () => fetchMyShifts(email, todayLocal()),
  })
  const referenceQuery = useQuery({
    queryKey: ['reference', departmentId],
    queryFn: async () => {
      const [teams, positions] = await Promise.all([fetchTeams(departmentId), fetchPositions(departmentId)])
      return { teams, positions }
    },
  })

  const myWeekHours = useMemo(() => {
    const totals = new Map<string, number>()
    for (const s of myShiftsQuery.data ?? []) {
      totals.set(s.weekId, round2((totals.get(s.weekId) ?? 0) + s.durationHours))
    }
    return totals
  }, [myShiftsQuery.data])

  if (openQuery.isPending || referenceQuery.isPending) return <Spinner label="Checking the callboard…" />
  if (openQuery.isError) return <ErrorBanner message="Couldn't load open shifts. Try again." />

  const teams = referenceQuery.data?.teams ?? []
  const positions = referenceQuery.data?.positions ?? []
  const threshold = employee.otThresholdHours ?? 40
  const now = Date.now()

  const visible = (openQuery.data ?? [])
    .filter((s) => Date.parse(s.endAt) > now)
    .filter((s) => !myPositionsOnly || employee.positionIds.includes(s.positionId))

  const request = async (shift: Shift) => {
    setBusyId(shift.id)
    try {
      await api.requestClaim({ shiftId: shift.id })
      setRequestedIds((prev) => new Set(prev).add(shift.id))
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['openShifts', departmentId] }),
        queryClient.invalidateQueries({ queryKey: ['requests', 'mine', email] }),
      ])
      toast('Request sent — your supervisor will review it.', 'success')
    } catch (e) {
      toast(claimErrorMessage(e), 'error')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-bold text-interlochen">Open shifts</h1>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={myPositionsOnly}
            onChange={(e) => setMyPositionsOnly(e.target.checked)}
            className="h-4 w-4 accent-[#004A7B]"
          />
          My positions only
        </label>
      </div>

      {visible.length === 0 ? (
        <EmptyState
          title="Nothing open right now."
          hint="Shifts appear here when your supervisor posts them."
        />
      ) : (
        <ul className="space-y-3" aria-label="Open shifts">
          {visible.map((shift) => {
            const teamName = teams.find((t) => t.id === shift.teamId)?.name
            const position = positions.find((p) => p.id === shift.positionId)
            const current = myWeekHours.get(shift.weekId) ?? 0
            const wouldCross = crossesThreshold(current, shift.durationHours, threshold)
            const projected = round2(current + shift.durationHours)
            const requested = requestedIds.has(shift.id)
            return (
              <li key={shift.id} className="card flex flex-col gap-3 sm:flex-row sm:items-center">
                <span
                  className="hidden h-12 w-1 shrink-0 rounded-full sm:block"
                  style={{ backgroundColor: positionColor(position ?? { color: null, id: shift.positionId }) }}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-interlochen">{formatDateLong(shift.date)}</p>
                  <p className="tnum text-sm font-semibold text-ink">
                    {formatTimeRange(shift.startTime, shift.endTime, shift.endsNextDay)}
                    <span className="tnum ml-2 font-normal text-stone">{formatHours(shift.durationHours)}</span>
                  </p>
                  <p className="truncate text-sm text-ink">
                    {shift.positionName}
                    {teamName && <span className="text-stone"> · {teamName}</span>}
                  </p>
                  {shift.claimCount > 0 && (
                    <p className="text-xs text-clay">
                      {shift.claimCount} other{shift.claimCount > 1 ? 's' : ''} requested
                    </p>
                  )}
                  {wouldCross && (
                    <p className="mt-1 text-xs font-semibold text-danger">
                      ⚠ would put you at {formatHours(projected)} — needs director sign-off
                    </p>
                  )}
                </div>
                <button
                  className="btn-primary shrink-0"
                  onClick={() => void request(shift)}
                  disabled={requested || busyId === shift.id}
                >
                  {requested ? 'Requested' : busyId === shift.id ? 'Sending…' : 'Request'}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
